import { Divider, Space, Typography } from "antd";
import type { CSSProperties, HTMLAttributes, ReactNode } from "react";

import useResponsive from "@/hooks/useResponsive.hook";
import IconGenerator from "./IconGenerator";

const { Text } = Typography;

type SectionHeaderSize = "small" | "middle" | "large";
type SectionHeaderAlign = "start" | "center" | "end";

type SectionHeaderProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: string | null;
  iconColor?: string;
  iconSize?: number;
  extra?: ReactNode;
  size?: SectionHeaderSize;
  align?: SectionHeaderAlign;
  showDivider?: boolean;
  dividerColor?: string;
  titleColor?: string;
  subtitleColor?: string;
  ellipsis?: boolean;
  contentStyle?: CSSProperties;
  dividerStyle?: CSSProperties;
};

const sizeConfig: Record<
  SectionHeaderSize,
  {
    gap: number;
    iconSize: number;
    titleFontSize: number;
    subtitleFontSize: number;
  }
> = {
  small: {
    gap: 8,
    iconSize: 16,
    titleFontSize: 12,
    subtitleFontSize: 11,
  },
  middle: {
    gap: 12,
    iconSize: 18,
    titleFontSize: 14,
    subtitleFontSize: 12,
  },
  large: {
    gap: 16,
    iconSize: 20,
    titleFontSize: 16,
    subtitleFontSize: 13,
  },
};

const justifyContentByAlign: Record<
  SectionHeaderAlign,
  CSSProperties["justifyContent"]
> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
};

export function SectionHeader({
  title,
  subtitle,
  icon = "UilUser",
  iconColor = "var(--color-disabled, #A2A7B5)",
  iconSize,
  extra,
  size = "middle",
  align = "start",
  showDivider = true,
  dividerColor = "var(--color-border)",
  titleColor = "var(--color-disabled, #A2A7B5)",
  subtitleColor = "var(--color-disabled, #A2A7B5)",
  ellipsis = true,
  className,
  style,
  contentStyle,
  dividerStyle,
  ...rest
}: SectionHeaderProps) {
  const isResponsive = useResponsive();
  const config = sizeConfig[size];
  const resolvedTitleFontSize = isResponsive
    ? Math.max(config.titleFontSize - 2, 11)
    : config.titleFontSize;
  const resolvedIconSize = iconSize ?? config.iconSize;

  return (
    <div className={className} style={{ width: "100%", ...style }} {...rest}>
      <div
        style={{
          alignItems: subtitle ? "flex-start" : "center",
          display: "flex",
          gap: config.gap,
          justifyContent: justifyContentByAlign[align],
          minWidth: 0,
          width: "100%",
          ...contentStyle,
        }}
      >
        <Space
          align={subtitle ? "start" : "center"}
          size={8}
          style={{ flex: "0 1 auto", minWidth: 0 }}
        >
          {icon ? (
            <IconGenerator
              name={icon}
              color={iconColor}
              size={resolvedIconSize}
            />
          ) : null}

          <span style={{ display: "grid", minWidth: 0 }}>
            <Text
              ellipsis={ellipsis}
              style={{
                color: titleColor,
                fontSize: resolvedTitleFontSize,
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {title}
            </Text>

            {subtitle ? (
              <Text
                ellipsis={ellipsis}
                style={{
                  color: subtitleColor,
                  fontSize: config.subtitleFontSize,
                  lineHeight: 1.4,
                }}
              >
                {subtitle}
              </Text>
            ) : null}
          </span>
        </Space>

        {showDivider ? (
          <Divider
            aria-hidden="true"
            style={{
              borderBlockStart: `1px solid ${dividerColor}`,
              flex: 1,
              minWidth: 12,
              ...dividerStyle,
            }}
          />
        ) : null}

        {extra ? <div style={{ flex: "0 0 auto" }}>{extra}</div> : null}
      </div>
    </div>
  );
}

export type { SectionHeaderProps };
